import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { jobApi } from '../../../api/job';
import Navbar from '../../layout/Navbar';
import JiraTicketModal from './JiraTicketModal';
import TestCaseDetailsModal from './TestCaseDetailsModal';
import { getJiraTickets } from '../../../api/integrations';

function JobTestCaseList() {
  const [jobTestCases, setJobTestCases] = useState([]);
  const [jobStatus, setJobStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { jobId } = useParams();
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [expandedScreenshots, setExpandedScreenshots] = useState(new Set());
  const [selectedTestCases, setSelectedTestCases] = useState(new Set());
  const navigate = useNavigate();
  const [isHealing, setIsHealing] = useState(false);
  const [jiraTicketModalOpen, setJiraTicketModalOpen] = useState(false);
  const [testCaseModalOpen, setTestCaseModalOpen] = useState(false);
  const [selectedTestCase, setSelectedTestCase] = useState(null);
  const [testCasesWithJiraTickets, setTestCasesWithJiraTickets] = useState(new Map());

  useEffect(() => {
    let pollInterval;
    
    // Start polling if job is not completed/failed/cancelled OR if healing is in progress
    const shouldPoll = (jobStatus !== 'completed' && jobStatus !== 'failed' && jobStatus !== 'cancelled') || isHealing;
    
    if (shouldPoll) {
      // Only do immediate fetch if not currently starting healing
      if (!isHealing || jobStatus === 'healing') {
        fetchJobStatus();
      }
      
      // Set up polling interval
      pollInterval = setInterval(fetchJobStatus, 10000);
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [jobId, jobStatus, isHealing]); // Dependencies

  // Load Jira ticket counts for test cases when job is complete or failed
  useEffect(() => {
    if ((jobStatus === 'completed' || jobStatus === 'failed') && jobTestCases.length > 0) {
      fetchJiraTicketsForTestCases();
    }
  }, [jobStatus, jobTestCases]);

  const fetchJiraTicketsForTestCases = async () => {
    // Create a Map to store test case ID to ticket count
    const ticketMap = new Map();

    // Get Jira tickets for each test case
    await Promise.all(jobTestCases.map(async (testCase) => {
      try {
        const response = await getJiraTickets(testCase.test_case_id);
        console.log(`Jira tickets for test case ${testCase.test_case_id}:`, response);
        
        // Handle different response formats to get tickets count
        let ticketCount = 0;
        
        if (Array.isArray(response)) {
          // If the response is already an array of tickets
          ticketCount = response.length;
        } else if (response.tickets && Array.isArray(response.tickets)) {
          ticketCount = response.tickets.length;
        } else if (response.jira_tickets && Array.isArray(response.jira_tickets)) {
          ticketCount = response.jira_tickets.length;
        } else if (typeof response === 'string') {
          try {
            // Sometimes the API may return a string that needs to be parsed
            const parsedResponse = JSON.parse(response);
            if (Array.isArray(parsedResponse)) {
              ticketCount = parsedResponse.length;
            } else if (parsedResponse.tickets) {
              ticketCount = parsedResponse.tickets.length;
            } else if (parsedResponse.jira_tickets) {
              ticketCount = parsedResponse.jira_tickets.length;
            }
          } catch (parseError) {
            console.error("Error parsing response string:", parseError);
          }
        }
        
        if (ticketCount > 0) {
          ticketMap.set(testCase.id, ticketCount);
        }
      } catch (err) {
        console.error(`Error fetching Jira tickets for test case ${testCase.test_case_id}:`, err);
        // Silently fail - we just won't show ticket count for this test case
      }
    }));

    setTestCasesWithJiraTickets(ticketMap);
  };

  const fetchJobStatus = async () => {
    try {
      const response = await jobApi.getJobStatus(jobId);
      
      setJobStatus(response.job.status);
      setJobTestCases(response.test_cases || []);
      setLoading(false);

      if (response.job.status === 'completed' || response.job.status === 'failed' || response.job.status === 'cancelled') {
        setIsHealing(false);
      }
    } catch (error) {
      console.error('Error fetching job test cases:', error);
      setError('Failed to load job test cases');
      setLoading(false);
      setIsHealing(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleRowExpansion = (testCaseId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(testCaseId)) {
        newSet.delete(testCaseId);
      } else {
        newSet.add(testCaseId);
      }
      return newSet;
    });
  };

  const toggleScreenshotsExpansion = (testCaseId, e) => {
    e.stopPropagation();
    setExpandedScreenshots(prev => {
      const newSet = new Set(prev);
      if (newSet.has(testCaseId)) {
        newSet.delete(testCaseId);
      } else {
        newSet.add(testCaseId);
      }
      return newSet;
    });
  };

  const toggleTestCaseSelection = (e, testCaseId) => {
    e.stopPropagation(); // Prevent row expansion when clicking checkbox
    setSelectedTestCases(prev => {
      const newSet = new Set(prev);
      if (newSet.has(testCaseId)) {
        newSet.delete(testCaseId);
      } else {
        newSet.add(testCaseId);
      }
      return newSet;
    });
  };

  const handleEditTestCase = (testCaseId, testSuiteId) => {
    // Navigate to edit test case page with the correct path format
    navigate(`/testcase/${testCaseId}/edit?testSuiteId=${testSuiteId}`);
  };

  const handleHealTests = async () => {
    try {
      const selectedTestCasesArray = Array.from(selectedTestCases);
      const selectedTestCasesDetails = jobTestCases.filter(tc => selectedTestCasesArray.includes(tc.id));

      if (selectedTestCasesDetails.length === 0) {
        throw new Error('No test cases selected');
      }

      // Update the test cases status to healing first
      setJobTestCases(prevTestCases => 
        prevTestCases.map(tc => {
          if (selectedTestCasesArray.includes(tc.id)) {
            return {
              ...tc,
              execution_status: 'healing',
              test_case_status: 'pending'
            };
          }
          return tc;
        })
      );

      // Call the heal test cases endpoint
      await jobApi.healTestCases(
        jobId,
        selectedTestCasesDetails.map(tc => tc.test_case_id)
      );

      // Set job status to healing
      setIsHealing(true);
      setJobStatus('healing');
      
      // Clear the test case selection
      setSelectedTestCases(new Set());

      // Wait for 3 seconds before starting to poll
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Do an initial fetch after the delay
      await fetchJobStatus();

    } catch (error) {
      console.error('Error healing tests:', error);
      alert('Failed to heal tests: ' + error.message);
      setIsHealing(false); // Reset healing state on error
    }
  };

  const parseScreenshots = (screenshotsString) => {
    if (!screenshotsString) return [];
    try {
      return JSON.parse(screenshotsString);
    } catch (error) {
      console.error('Error parsing screenshots:', error);
      return [];
    }
  };

  const handleSelectAll = () => {
    if (selectedTestCases.size === jobTestCases.length) {
      // If all are selected, deselect all
      setSelectedTestCases(new Set());
    } else {
      // Select all test cases
      setSelectedTestCases(new Set(jobTestCases.map(tc => tc.id)));
    }
  };

  const handleCreateJiraIssues = () => {
    if (selectedTestCases.size === 0) {
      alert('Please select at least one test case');
      return;
    }
    
    setJiraTicketModalOpen(true);
  };

  const handleOpenTestCaseDetails = (testCase) => {
    setSelectedTestCase(testCase);
    setTestCaseModalOpen(true);
  };

  const handleJiraTicketCreated = () => {
    // Refresh the Jira ticket counts
    fetchJiraTicketsForTestCases();
  };

  // Function to handle cancelling the entire job
  const handleCancelJob = async () => {
    try {
      // Show cancelling state immediately
      setJobStatus('cancelling');
      
      // Also update the status of all running or pending test cases to cancelling
      setJobTestCases(prevTestCases => 
        prevTestCases.map(tc => {
          if (tc.execution_status === 'running' || tc.execution_status === 'pending') {
            return {
              ...tc,
              execution_status: 'cancelling'
            };
          }
          return tc;
        })
      );
      
      // Call the API to cancel the job
      await jobApi.cancelJob(jobId);
      
      // Fetch updated job status - don't update UI state yet so we don't hide the button
      await fetchJobStatus();
    } catch (error) {
      console.error('Error cancelling job:', error);
      alert('Failed to cancel job: ' + error.message);
      // Revert UI on error
      await fetchJobStatus();
    }
  };
  
  // Function to determine if the job can be cancelled
  const canCancelJob = () => {
    return jobStatus === 'running' || 
           jobStatus === 'pending' || 
           jobStatus === 'healing' ||
           jobStatus === 'cancelling';  // Keep showing when cancelling
  };
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <Link to="/testsuites" className="text-gray-600 hover:text-black">
              Test Suites
            </Link>
            <span className="text-gray-600">/</span>
            <h1 className="text-2xl font-bold text-gray-900">
              Job Status: {' '}
              <span className={`text-sm px-2 py-1 rounded-full 
                ${jobStatus === 'completed' ? 'bg-green-100 text-green-800' : 
                  jobStatus === 'failed' ? 'bg-red-100 text-red-800' : 
                  jobStatus === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                  jobStatus === 'cancelling' ? 'bg-gray-200 text-gray-800 animate-pulse' : 
                  'bg-yellow-100 text-yellow-800'}`}>
                {jobStatus === 'cancelling' ? 'Cancelling...' : jobStatus}
              </span>
            </h1>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
          {/* Test Statistics Cards */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Total Tests</h3>
            <p className="text-2xl font-bold">{jobTestCases.length}</p>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Passed</h3>
            <p className="text-2xl font-bold text-green-600">
              {jobTestCases.filter(test => test.test_case_status === 'passed').length}
            </p>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Failed</h3>
            <p className="text-2xl font-bold text-red-600">
              {jobTestCases.filter(test => test.test_case_status === 'failed').length}
            </p>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Success Rate</h3>
            <p className="text-2xl font-bold text-blue-600">
              {jobTestCases.length > 0 && jobTestCases.filter(test => test.test_case_status !== 'cancelled' && test.test_case_status !== 'pending').length > 0
                ? `${Math.round((jobTestCases.filter(test => test.test_case_status === 'passed').length / jobTestCases.filter(test => test.test_case_status !== 'cancelled' && test.test_case_status !== 'pending').length) * 100)}%`
                : '0%'
              }
            </p>
          </div>
        </div>

        {selectedTestCases.size > 0 && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-center justify-between">
            <div className="text-blue-700 font-medium">
              {selectedTestCases.size} {selectedTestCases.size === 1 ? 'test case' : 'test cases'} selected
            </div>
            <button
              onClick={() => setSelectedTestCases(new Set())}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              Clear selection
            </button>
          </div>
        )}

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <button
            onClick={handleSelectAll}
            className="px-4 py-2 rounded-md text-white bg-gray-600 hover:bg-gray-700"
          >
            {selectedTestCases.size === jobTestCases.length ? 'Deselect All' : 'Select All'}
          </button>
          
          {selectedTestCases.size > 0 && (
            <>
              <button 
                onClick={() => {
                  const testCaseId = Array.from(selectedTestCases)[0];
                  // Find the selected test case to get its test_case_id
                  const selectedTestCase = jobTestCases.find(tc => tc.id === testCaseId);
                  if (selectedTestCase) {
                    handleEditTestCase(
                      selectedTestCase.test_case_id, 
                      jobTestCases[0]?.job_id ? jobTestCases[0].job_id : jobId
                    );
                  }
                }}
                disabled={selectedTestCases.size !== 1}
                className={`px-4 py-2 rounded-md text-white ${
                  selectedTestCases.size === 1 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
              >
                Edit Test Case
              </button>
              <button 
                onClick={handleHealTests}
                disabled={isHealing}
                className={`px-4 py-2 rounded-md text-white flex items-center ${
                  !isHealing
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
              >
                {isHealing ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Healing...
                  </>
                ) : (
                  'Heal Tests'
                )}
              </button>
              <button
                onClick={handleCreateJiraIssues}
                className="px-4 py-2 rounded-md text-white bg-indigo-600 hover:bg-indigo-700 flex items-center"
                title="Create Jira tickets for selected test cases"
              >
                <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L4 7L12 12L20 7L12 2Z" fill="white"/>
                  <path d="M4 7V17L12 22L20 17V7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Create Jira Issues
              </button>
            </>
          )}
          {canCancelJob() && (
            <button 
              onClick={handleCancelJob}
              className="px-4 py-2 rounded-md text-white bg-red-600 hover:bg-red-700 flex items-center"
              disabled={jobStatus === 'cancelling'}
            >
              {jobStatus === 'cancelling' ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  Cancelling...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Cancel All Tests
                </>
              )}
            </button>
          )}

        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading job status...</p>
          </div>
        ) : error ? (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
            {error}
          </div>
        ) : jobTestCases.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <p className="text-gray-600">No test cases found for this job.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {/* Desktop: table layout */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Select
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Test Case
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Execution Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Test Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Message
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Screenshots
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Started At
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Completed At
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {jobTestCases.map((testCase) => {
                    const screenshots = parseScreenshots(testCase.screenshots);
                    const jiraTicketCount = testCasesWithJiraTickets.get(testCase.id) || 0;
                    
                    return (
                      <React.Fragment key={testCase.id}>
                        <tr 
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleOpenTestCaseDetails(testCase)}
                        >
                          <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              checked={selectedTestCases.has(testCase.id)}
                              onChange={(e) => toggleTestCaseSelection(e, testCase.id)}
                            />
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900 flex items-center">
                              {testCase.test_case_details.testname}
                              
                              {jiraTicketCount > 0 && (
                                <div className="ml-2 flex items-center" title="Jira tickets">
                                  <svg className="h-4 w-4 text-blue-600" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 2L4 7L12 12L20 7L12 2Z" fill="#2684FF"/>
                                    <path d="M4 7V17L12 22L20 17V7" stroke="#2684FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                  <span className="text-xs bg-blue-100 text-blue-800 ml-1 px-1.5 rounded-full">
                                    {jiraTicketCount}
                                  </span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                              ${testCase.execution_status === 'completed' ? 'bg-green-100 text-green-800' : 
                                testCase.execution_status === 'failed' ? 'bg-red-100 text-red-800' : 
                                testCase.execution_status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                                testCase.execution_status === 'cancelling' ? 'bg-gray-200 text-gray-800 animate-pulse' : 
                                'bg-yellow-100 text-yellow-800'}`}
                            >
                              {testCase.execution_status === 'cancelling' ? 'Cancelling...' : testCase.execution_status}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                              ${testCase.test_case_status === 'passed' ? 'bg-green-100 text-green-800' : 
                                testCase.test_case_status === 'failed' ? 'bg-red-100 text-red-800' : 
                                testCase.test_case_status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                                'bg-yellow-100 text-yellow-800'}`}
                            >
                              {testCase.test_case_status || 'pending'}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-500">
                              {testCase.message ? (
                                <>
                                  <div className="max-w-xs truncate">
                                    {testCase.message}
                                  </div>
                                  {testCase.message.length > 50 && (
                                    <button 
                                      className="mt-1 text-blue-600 hover:text-blue-800 text-xs flex items-center"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleRowExpansion(testCase.id);
                                      }}
                                    >
                                      <span>See more</span>
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                      </svg>
                                    </button>
                                  )}
                                </>
                              ) : (
                                <span className="text-gray-400">No message</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 cursor-pointer" onClick={(e) => toggleScreenshotsExpansion(testCase.id, e)}>
                            {screenshots.length > 0 && (
                              <div className="bg-blue-600 text-white hover:bg-blue-700 text-sm flex items-center border border-blue-600 rounded-md px-3 py-1 transition-colors">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                <span>View Screenshots ({screenshots.length})</span>
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">
                              {formatDate(testCase.started_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">
                              {testCase.completed_at ? formatDate(testCase.completed_at) : '-'}
                            </div>
                          </td>
                        </tr>
                        {/* Expanded row for message */}
                        {expandedRows.has(testCase.id) && (
                          <tr className="bg-gray-50">
                            <td colSpan="8" className="px-6 py-4">
                              <div className="text-sm text-gray-700 whitespace-pre-wrap">
                                <div className="font-medium mb-2">Full Message:</div>
                                {testCase.message}
                              </div>
                              <button 
                                className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleRowExpansion(testCase.id);
                                }}
                              >
                                Show less
                              </button>
                            </td>
                          </tr>
                        )}
                        {/* Expanded row for screenshots */}
                        {expandedScreenshots.has(testCase.id) && screenshots.length > 0 && (
                          <tr className="bg-gray-50">
                            <td colSpan="8" className="px-6 py-4">
                              <div className="font-medium mb-4">Screenshots:</div>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {screenshots.map((screenshot, index) => {
                                  // Mask password values in step names
                                  let displayStepName = screenshot.step_name;
                                  if (displayStepName && displayStepName.toLowerCase().includes('password')) {
                                    // Match patterns like: Type "any-text-here" into password
                                    displayStepName = displayStepName.replace(
                                      /(Type\s+")([^"]+)("(?:\s+into)?\s+password)/i,
                                      (match, prefix, value, suffix) => `${prefix}${'•'.repeat(Math.min(10, value.length))}${suffix}`
                                    );
                                  }
                                  
                                  return (
                                    <div key={index} className="border rounded-lg overflow-hidden">
                                      <div className="p-2 bg-gray-100 border-b">
                                        <p className="text-sm font-medium">{displayStepName}</p>
                                        <p className="text-xs text-gray-500">{new Date(screenshot.timestamp).toLocaleString()}</p>
                                      </div>
                                      <a href={screenshot.url} target="_blank" rel="noopener noreferrer" className="block">
                                        <img 
                                          src={screenshot.url} 
                                          alt={`Step: ${displayStepName}`} 
                                          className="w-full h-auto object-contain"
                                          loading="lazy"
                                        />
                                      </a>
                                    </div>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Narrow screens: card layout with all columns and View Screenshots button visible */}
            <div className="lg:hidden divide-y divide-gray-200">
              {jobTestCases.map((testCase) => {
                const screenshots = parseScreenshots(testCase.screenshots);
                const jiraTicketCount = testCasesWithJiraTickets.get(testCase.id) || 0;
                
                return (
                  <div key={testCase.id}>
                    <div
                      className="px-4 py-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleOpenTestCaseDetails(testCase)}
                    >
                      <div className="flex flex-wrap items-start gap-x-4 gap-y-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded flex-shrink-0"
                            checked={selectedTestCases.has(testCase.id)}
                            onChange={(e) => toggleTestCaseSelection(e, testCase.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 flex items-center flex-wrap gap-2">
                              {testCase.test_case_details.testname}
                              {jiraTicketCount > 0 && (
                                <div className="flex items-center" title="Jira tickets">
                                  <svg className="h-4 w-4 text-blue-600" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 2L4 7L12 12L20 7L12 2Z" fill="#2684FF"/>
                                    <path d="M4 7V17L12 22L20 17V7" stroke="#2684FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                  <span className="text-xs bg-blue-100 text-blue-800 px-1.5 rounded-full">
                                    {jiraTicketCount}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium
                                ${testCase.execution_status === 'completed' ? 'bg-green-100 text-green-800' : 
                                  testCase.execution_status === 'failed' ? 'bg-red-100 text-red-800' : 
                                  testCase.execution_status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                                  testCase.execution_status === 'cancelling' ? 'bg-gray-200 text-gray-800 animate-pulse' : 
                                  'bg-yellow-100 text-yellow-800'}`}>
                                {testCase.execution_status === 'cancelling' ? 'Cancelling...' : testCase.execution_status}
                              </span>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium
                                ${testCase.test_case_status === 'passed' ? 'bg-green-100 text-green-800' : 
                                  testCase.test_case_status === 'failed' ? 'bg-red-100 text-red-800' : 
                                  testCase.test_case_status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                                  'bg-yellow-100 text-yellow-800'}`}>
                                {testCase.test_case_status || 'pending'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
                          <div className="text-xs text-gray-500">
                            <div>Started: {formatDate(testCase.started_at)}</div>
                            <div>Completed: {testCase.completed_at ? formatDate(testCase.completed_at) : '-'}</div>
                          </div>
                          <div className="flex-shrink-0">
                            {screenshots.length > 0 && (
                              <div
                                className="bg-blue-600 text-white hover:bg-blue-700 text-sm flex items-center justify-center border border-blue-600 rounded-md px-4 py-2 transition-colors w-full sm:w-auto"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleScreenshotsExpansion(testCase.id, e);
                                }}
                              >
                                <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                <span>View Screenshots ({screenshots.length})</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {testCase.message && (
                        <div className="mt-3 text-sm text-gray-500">
                          {expandedRows.has(testCase.id) ? (
                            <>
                              <div className="whitespace-pre-wrap">{testCase.message}</div>
                              <button
                                className="mt-1 text-blue-600 hover:text-blue-800 text-xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleRowExpansion(testCase.id);
                                }}
                              >
                                Show less
                              </button>
                            </>
                          ) : (
                            <>
                              <div className="line-clamp-2">{testCase.message}</div>
                              {testCase.message.length > 50 && (
                                <button
                                  className="mt-1 text-blue-600 hover:text-blue-800 text-xs flex items-center"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleRowExpansion(testCase.id);
                                  }}
                                >
                                  <span>See more</span>
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    {expandedScreenshots.has(testCase.id) && screenshots.length > 0 && (
                      <div className="px-4 pb-4 bg-gray-50 border-t border-gray-200">
                        <div className="font-medium mb-4">Screenshots:</div>
                        <div className="grid grid-cols-1 gap-4">
                          {screenshots.map((screenshot, index) => {
                            let displayStepName = screenshot.step_name;
                            if (displayStepName && displayStepName.toLowerCase().includes('password')) {
                              displayStepName = displayStepName.replace(
                                /(Type\s+")([^"]+)("(?:\s+into)?\s+password)/i,
                                (match, prefix, value, suffix) => `${prefix}${'•'.repeat(Math.min(10, value.length))}${suffix}`
                              );
                            }
                            return (
                              <div key={index} className="border rounded-lg overflow-hidden bg-white">
                                <div className="p-2 bg-gray-100 border-b">
                                  <p className="text-sm font-medium">{displayStepName}</p>
                                  <p className="text-xs text-gray-500">{new Date(screenshot.timestamp).toLocaleString()}</p>
                                </div>
                                <a href={screenshot.url} target="_blank" rel="noopener noreferrer" className="block">
                                  <img 
                                    src={screenshot.url} 
                                    alt={`Step: ${displayStepName}`} 
                                    className="w-full h-auto object-contain"
                                    loading="lazy"
                                  />
                                </a>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Jira Ticket Creation Modal */}
      <JiraTicketModal
        isOpen={jiraTicketModalOpen}
        onClose={() => setJiraTicketModalOpen(false)}
        selectedTestCases={jobTestCases.filter(tc => selectedTestCases.has(tc.id))}
        onSuccess={handleJiraTicketCreated}
      />

      {/* Test Case Details Modal */}
      <TestCaseDetailsModal
        isOpen={testCaseModalOpen}
        onClose={() => setTestCaseModalOpen(false)}
        testCase={selectedTestCase}
      />
    </div>
  );
}

export default JobTestCaseList;